import { useState } from "react";
import { useT, translateError } from "../../i18n/hooks.ts";
import { mappingCandidates, type SubBasinColumnMapping } from "../../model/projectEntities.ts";
import {
  linkToSubBasins,
  geometryKey,
  geometriesOfLiveEntities,
  type SubBasinLayer,
} from "../../model/gisLayer.ts";
import type { SubBasin } from "../../model/types.ts";
import { FileButton } from "../FileButton.tsx";
import { InfoIcon } from "../InfoIcon.tsx";
import {
  loadGisFiles,
  chooseInitialIdField,
  geojsonFromLayerWithAttributes,
} from "../gisUpload.ts";
import { FieldsPreviewTable } from "./FieldsPreviewTable.tsx";

/**
 * Links a shapefile with the project's ALREADY EXISTING sub-basins (unlike
 * Step1SubBasins, which creates the sub-basins from scratch). Needed when
 * a project is opened without a linkable GIS layer, or to replace the one
 * it came with. If a layer is already loaded (`gisLayer`, even if
 * unlinked), it's used directly — no need to re-upload the shapefile just
 * to try another link column, because `gisLayer` already stores each
 * feature's geometry and the .dbf's raw attributes (and all of that lives
 * in IndexedDB, not just in this session's memory). A file only needs
 * uploading when there's no layer yet, or to replace it with a different
 * one.
 *
 * Also used to recover permanently deleted sub-basins (see projectEntities.ts):
 * uploading the same original shapefile compares its geometries against
 * the ones already in `gisLayer` (geometryKey/geometriesOf, gisLayer.ts)
 * and only offers to add the missing ones — avoiding duplicates for ones
 * that were never deleted. Starting from `gisLayer` itself finds nothing
 * to recover, as expected (there's nothing "new" that isn't already
 * there) — the real file still needs uploading for that.
 */
export function GisLayerPanel({
  subBasins,
  gisLayer,
  onLinked,
  onRecover,
}: {
  subBasins: SubBasin[];
  gisLayer: SubBasinLayer | null;
  onLinked: (layer: SubBasinLayer) => void;
  onRecover: (geojson: GeoJSON.FeatureCollection, mapping: SubBasinColumnMapping) => void;
}) {
  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection | null>(() =>
    gisLayer && gisLayer.features.length > 0 ? geojsonFromLayerWithAttributes(gisLayer) : null
  );
  const [fieldId, setFieldId] = useState(() => {
    if (!gisLayer || gisLayer.features.length === 0) return "";
    const initialFc = geojsonFromLayerWithAttributes(gisLayer);
    const { numericFields, textFields } = mappingCandidates(initialFc);
    return chooseInitialIdField(
      numericFields,
      textFields,
      (campo) => linkToSubBasins(initialFc, subBasins, campo).linked
    );
  });
  const [recoverMapping, setRecoverMapping] = useState<SubBasinColumnMapping>({
    nameField: "",
    areaField: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [mode, setMode] = useState<"link" | "recover">("link");
  const t = useT();

  async function onFiles(files: FileList | null) {
    if (!files) return;
    setError(null);
    setNotice(null);
    try {
      const { geojson: fc, notice: loadedNotice } = await loadGisFiles(files, t);
      setGeojson(fc);
      setNotice(loadedNotice);
      const { numericFields, textFields } = mappingCandidates(fc);
      setFieldId(
        chooseInitialIdField(
          numericFields,
          textFields,
          (campo) => linkToSubBasins(fc, subBasins, campo).linked
        )
      );
      setRecoverMapping({ nameField: textFields[0] ?? "", areaField: numericFields[0] ?? "" });
    } catch (err) {
      // translateError() renders a user-facing message; also logged (deep-
      // review, 2026-08-29) since it can collapse a richer error (a real
      // shapefile parse failure, a malformed .dbf) into a generic one.
      console.error("Could not load GIS files:", err);
      setError(translateError(err, t));
      setGeojson(null);
    }
  }

  const previewLayer = geojson && fieldId ? linkToSubBasins(geojson, subBasins, fieldId) : null;
  const { textFields: textFields, numericFields: numericFields } = geojson
    ? mappingCandidates(geojson)
    : { textFields: [], numericFields: [] };
  const availableFields = [...new Set([...numericFields, ...textFields])];

  const existingGeometries = geometriesOfLiveEntities(gisLayer, subBasins);
  const missingFeatures =
    geojson?.features.filter(
      (f) => !existingGeometries.has(geometryKey(f.geometry as GeoJSON.Geometry))
    ) ?? [];

  return (
    <div>
      <p>
        {gisLayer && gisLayer.features.length > 0
          ? t("gisLayerPanel.layerAlreadyExists")
          : t("gisLayerPanel.uploadShapefile")}
      </p>
      <FileButton multiple accept=".shp,.dbf,.prj,.shx,.cpg,.geojson,.json" onFiles={onFiles} />
      {error && <p className="error">{error}</p>}
      {notice && <p className="engine-notice">{notice}</p>}

      {geojson && (
        <div className="step-mapping">
          <div className="edit-panel-tabs">
            <button
              className={mode === "link" ? "tab-active" : undefined}
              onClick={() => setMode("link")}
            >
              {t("gisLayerPanel.modeLink")}
            </button>
            <button
              className={mode === "recover" ? "tab-active" : undefined}
              onClick={() => setMode("recover")}
            >
              {t("gisLayerPanel.modeRecover")}
              {missingFeatures.length > 0 && ` (${missingFeatures.length})`}
            </button>
          </div>

          {mode === "link" ? (
            <>
              <label>
                {t("gisLayerPanel.linkColumn")}
                <InfoIcon text={t("gisLayerPanel.linkColumnHelp")} />
                <select value={fieldId} onChange={(e) => setFieldId(e.target.value)}>
                  {availableFields.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              {previewLayer && (
                <p>
                  {t("gisLayerPanel.linkedCount", {
                    n: previewLayer.linked,
                    total: subBasins.length,
                  })}
                </p>
              )}

              <FieldsPreviewTable
                geojson={geojson}
                fields={availableFields}
                chosenField={fieldId}
              />

              <button
                className="button button-primary"
                disabled={!previewLayer || previewLayer.linked === 0}
                onClick={() => previewLayer && onLinked(previewLayer)}
              >
                {t("gisLayerPanel.linkLayer")}
              </button>
            </>
          ) : missingFeatures.length === 0 ? (
            <p className="muted-text">{t("gisLayerPanel.allAlreadyPresent")}</p>
          ) : (
            <>
              <p>
                {t("gisLayerPanel.missingCount", {
                  n: missingFeatures.length,
                  total: geojson.features.length,
                })}
              </p>

              <FieldsPreviewTable
                geojson={geojson}
                fields={availableFields}
                chosenField={recoverMapping.nameField}
              />

              <label>
                {t("gisLayerPanel.subBasinName")}
                <select
                  value={recoverMapping.nameField}
                  onChange={(e) =>
                    setRecoverMapping({ ...recoverMapping, nameField: e.target.value })
                  }
                >
                  {textFields.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t("resultsPanel.areaKm2")}
                <select
                  value={recoverMapping.areaField}
                  onChange={(e) =>
                    setRecoverMapping({ ...recoverMapping, areaField: e.target.value })
                  }
                >
                  {numericFields.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="button"
                disabled={!recoverMapping.nameField || !recoverMapping.areaField}
                onClick={() => geojson && onRecover(geojson, recoverMapping)}
              >
                {t(
                  missingFeatures.length > 1
                    ? "gisLayerPanel.recoverMultiple"
                    : "gisLayerPanel.recoverOne",
                  { n: missingFeatures.length }
                )}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
